/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useMemo, useEffect } from 'react';
import { isObjectArrayEqual } from 'utils/isObjectArrayEqual';
// material
import { Switch, TextField, Divider, Icon, ToolTip, displayModal } from '@insticator/insticator-ui';
import Space from 'material/space/space';
// content
import Products from './products/products';
import Placement from './placement/placement';
import ActionButtons from './actionbuttons/actionbuttons';
// styles
import injectSheet from 'react-jss'
import styles from './productgroup_styles'
import { useDispatch, useSelector } from 'react-redux';
import PublishWarning from '../../publishwarning/publishwarning';
import Devices from './devices/devices';
import ExpandableSection from 'material/expandablesection/expandablesection';
import { ConfigV2Controller } from 'material/expandablesection/v2';
import PreviewConfig from 'material/previewconfig/previewconfig';
import Status from 'material/status/status';
import { revertProductManagerConfigChanges } from 'state/slices/productmanager';
import Preview from './preview/preview';
import { isControlBarDraftLiveEqual, isProductGroupDraftLiveEqual } from 'utils/productgroup';
import DeleteProductGroupModal from '../deleteproductgroupmodal/deleteproductgroupmodal';


/* ========== ~~~~~~~~~~ MODERATION CONFIG V2: GENERAL SECTION ~~~~~~~~~~ ========== */
const ProductGroupV2 = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const { tempProductManagerConfig } = useSelector(state => state.productManager)
  const [isOpened, setIsOpened] = useState(false)
  const {
    index,
    group,
    previewModeCode,
    isDraftChanged,
    onChange,
    onFieldsChange,
    onRemove,
    siteURL,
    saveDraftText,
    controlBarConfig
  } = props;
  const [selectedMode, selectMode] = useState('draft')

  const selectedGroup = useMemo(() => group[selectedMode], [group, selectedMode])

  const handleProductGroupChange = (property, value) => onChange(property, value, selectedMode)

  const handleProductGroupPropertiesChange = (properties) => onFieldsChange(properties, selectedMode)

  const isDraftSavedOnce = !!group.groupId

  const disableSaveDraft = !isDraftChanged

  const [showToggleButtons, setShowToggleButtons] = useState(isDraftSavedOnce)

  const [isSetLiveShown, showSetLive] = useState(false)

  // const disableSetAsCurrent = isDraftChanged || isSetCurrentDisabled
  const disableSetAsCurrent = isDraftSavedOnce ? isObjectArrayEqual(group.draft, group.current) : true

  const onPublish = (action) => {
    if (action === 'saveDraft') {
      // dispatch(displayModal(false))
      showSetLive(true)
    } else {
      // dispatch(displayModal(false))
      selectMode('current')
      setShowToggleButtons(true)
      showSetLive(false)
    }
  }

  const showPublishWarning = (action = 'saveDraft') => dispatch(displayModal(true, <PublishWarning groupIndex={index} action={action} onPublish={() => onPublish(action)} />, false))

  const discardChange = async () => {
    await dispatch(revertProductManagerConfigChanges())
  }

  const handleToggle = () => setIsOpened(!isOpened)

  const handleCancel = () => {
    setIsOpened(!isOpened)
  }

  const handleSave = () => { }

  const status = useMemo(() => {
    const isDraftLiveEqual = isProductGroupDraftLiveEqual(group)
    const isGroupTriggerControlBar = group.draft.trigger === 'CONTROL_BAR'
    const isControlBarEqual = isControlBarDraftLiveEqual(controlBarConfig)

    if(isGroupTriggerControlBar) {
      if(isDraftSavedOnce && isDraftLiveEqual && isControlBarEqual) return 'LIVE'
      if(!isDraftSavedOnce) return 'PENDING'
      if(isDraftChanged) return 'EDITED'
      if(isDraftSavedOnce && (!isDraftLiveEqual ||!isControlBarEqual)) return 'TESTED'

    } else {
      if(isDraftSavedOnce && isDraftLiveEqual) return 'LIVE'
      if(!isDraftSavedOnce) return 'PENDING'
      if(isDraftChanged) return 'EDITED'
      if(isDraftSavedOnce && !isDraftLiveEqual) return 'TESTED'
    }

  },[controlBarConfig, group, isDraftChanged, isDraftSavedOnce])

  const showDeleteProductGroupConfirm = () => dispatch(displayModal(true, <DeleteProductGroupModal status={status} groupLabel={selectedGroup.label} onConfirm={onRemove} />))

  const isGroupNameExist = !!tempProductManagerConfig.groupConfigList.filter((existingGrp, idx) => {
    if(existingGrp.draft.label === group.draft.label && idx !== index) return true
    return false
  }).length

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      alwaysShowActionButtons
      actionButtons={
        <div onClick={e => e.stopPropagation()}>
          <Space gap={16} alignItems={'center'}>
            <Status status={status} />
            <ToolTip
              position={'top'}
              trigger={'mouseenter focus'}
              content={'Delete Product Group'}
            >
              <Icon name={'trash'} handleClick={() => showDeleteProductGroupConfirm()} />
            </ToolTip>
          </Space>
        </div>
      }
      title={
        <div onClick={e => isOpened ?  e.stopPropagation() : null}>
          <Space klass={`disabled-${selectedMode === 'current'}`} alignItems={isOpened ? 'baseline' : 'center'}>
            <Switch on={selectedGroup.groupEnabled} handleClick={()=> handleProductGroupChange('groupEnabled', !selectedGroup.groupEnabled)} variant={'primary'} />
            {isOpened ? 
              <Space direction={'column'} gap={0} style={{marginTop: '16px'}}>
                <TextField 
                  type={`text`} 
                  name={`productgrouplabel`} 
                  value={selectedGroup.label} 
                  handleChange={e => { handleProductGroupChange('label', e.target.value)}} 
                  label={''}
                  placeholder={`Enter group name`} 
                  maxLength={128}
                  disabled={!selectedGroup.groupEnabled}
                  valid={selectedGroup.label && !isGroupNameExist}
                />
                {!selectedGroup.label && <span className='errormessage'>Group name is required</span>}
                {selectedGroup.label && isGroupNameExist && <span className='errormessage'>Group name already exists.</span>}
              </Space>
            :
            <h3 className='groupname'>{selectedGroup.label}</h3>
            }
          </Space>
        </div>
      }
    />
  );

  const [products, selectProducts] = useState([])
  const [isProductOrdered, setIsProductOrdered] = useState(false)

  useEffect(() => {
    if(!isDraftChanged) {
      setIsProductOrdered(false)
      selectProducts([])
    }
  }, [isDraftChanged])

  useEffect(() => {
    // set the order of the products
    // TODO: Set article sentiment 2.0
    const { commentingOrder, trendingNowOrder, articleSentimentOrder } = selectedGroup

    if (!isProductOrdered && !products.length) {
      const productOrders = [
        {
          product: 'COMMENTING_V2',
          order: commentingOrder
        },
        {
          product: 'TRENDING_NOW_V2',
          order: trendingNowOrder
        },
        {
          product: 'ARTICLE_SENTIMENT_V2',
          order: articleSentimentOrder,
        }
      ]

      // Some of orders can be null. Consider as last if it is null
      const ordered = productOrders.sort((a, b) => {
        if (a.order === null) return 1;      // Move null to the end
        if (b.order === null) return -1;     // Move null to the end
        return a.order - b.order;
      })
      selectProducts(ordered.map(o => o.product))
      setIsProductOrdered(true)
    }
  }, [isProductOrdered, products, selectedGroup])

  return (
    <ExpandableSection
      className={`expandablesectionv2 productgroup ${props.classes.productgroup}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
    >
      <Space gap={48} direction={'row'}>
        <Space gap={32} direction={'column'}>
          <Devices deviceType={selectedGroup.deviceType} onChange={handleProductGroupChange} />
          <Placement
            products={products}
            groupId={group.groupId}
            {...selectedGroup}
            onChange={handleProductGroupChange}
            onFieldsChange={handleProductGroupPropertiesChange}
          />
          {isProductOrdered && (
            <Products
              showCommenting={selectedGroup.showCommenting}
              showTrendingNow={selectedGroup.showTrendingNow}
              showArticleSentiment={selectedGroup.showArticleSentiment}
              onChange={handleProductGroupChange}
              onFieldsChange={handleProductGroupPropertiesChange}
              onProductOrderChange={products => selectProducts(products)}
              products={products}
            />
          )}
        </Space>
        <PreviewConfig>
          <Preview
            layout={selectedGroup.layout || 'INLINE'}
            trigger={selectedGroup.trigger || 'NONE'}
            products={products}
            showCommenting={selectedGroup.showCommenting}
            showTrendingNow={selectedGroup.showTrendingNow}
            showArticleSentiment={selectedGroup.showArticleSentiment}
            controlBarPlacement={controlBarConfig?.draft?.desktop?.placement}
            controlBarAlignment={controlBarConfig?.draft?.desktop?.alignment}
          />
        </PreviewConfig>
      </Space>
      <Divider />
      <ActionButtons
        discardChange={discardChange}
        isDraftChanged={isDraftChanged}
      />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ProductGroupV2;